import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { AppLoginPrincipalService, AppLoginSubjectService, Pagination, TenantRoleLoginEPService,TenantRoleService, TenantTeamRoleService, TenantUserRoleService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { RoleModel } from '../../../../../models/role-permissions.model';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../listing.utils';
import { PaginationComponent } from '../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { Observable, of, forkJoin, iif, zip } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../permission-controller/root';
import { RolePermActions } from '../../../role-permissions-controller/actions';
import { RolePermSelectors } from '../../../role-permissions-controller/selectors';
import { RolePermStates } from '../../../role-permissions-controller/states';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-role-permission-listing',
  templateUrl: './role-permission-listing.component.html',
  styleUrls: ['./role-permission-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class RolePermissionListingComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;

  protected readonly index = 0;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => state.localState.deactivateList);

  toggleColumn$: Observable<boolean>;

  // api visa
  apiVisa = AppConfig.apiVisa;
  appletGuid = sessionStorage.getItem('appletGuid');
  // initial grid state
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'single';
  // sample row data
  rowData = [
    // {
    //   roles: "Admin",
    //   num_users: 1,
    //   num_teams: 1
    // },
    // {
    //   roles: "Owner",
    //   num_users: 2,
    //   num_teams: 1
    // },
    // {
    //   roles: "Visitor",
    //   num_users: 7,
    //   num_teams: 6
    // },
  ];
  searchValue: any;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  //translation variable
  create_button: string;
  search_text: string;
  search_placeholder: string;
  role_code_text: string;
  role_name_text: string;
  no_of_users_text: string;
  no_of_teams_text: string;

  constructor(
    // private appLoginPrincipalService: AppLoginPrincipalService,
    private appLoginSubjectService: AppLoginSubjectService,
    // private tenantRoleService: TenantRoleService,
    private tenantRoleService: TenantRoleLoginEPService,
    private tenantUserRoleService: TenantUserRoleService,
    private tenantTeamRoleService: TenantTeamRoleService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private readonly componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.create_button = res.create_button_nocaps;
        this.search_text = res.search;
        this.search_placeholder = res.search_role_or_name;
        this.role_code_text = res.role_code;
        this.role_name_text = res.role_name;
        this.no_of_users_text = res.no_of_users;
        this.no_of_teams_text = res.no_of_teams;
      });
    } else {
      this.create_button = 'Create';
      this.search_text = 'Search';
      this.search_placeholder = 'Search role code or name';
      this.role_code_text = 'Role Code';
      this.role_name_text = 'Role Name';
      this.no_of_users_text = 'No. of Users';
      this.no_of_teams_text = 'No. of Teams';
    }
  }

  /**
   * Lifecycle method: called when component is first initialised.
   */
   ngOnInit(): void {
    // initial configurations for toggle column and local state
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });

    // initialise column definitions
    this.columnsDefs = [
      {
        headerName: this.role_code_text,
        field: 'app_mst_role.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.role_name_text,
        field: 'app_mst_role.name',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName:this.no_of_users_text,
        field: 'num_users',
        headerClass: 'text-right',
        cellStyle: {textAlign: 'right'}
      },
      {
        headerName: this.no_of_teams_text,
        field: 'num_teams',
        headerClass: 'text-right',
        cellStyle: {textAlign: 'right'}
      }
    ];
  }

  /**
   * This function is called when the user clicks on the Add button in the UI.
   * Will trigger the add component to be shown on the screen.
   *
   */
   onNext() {
    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateAdd: true,
        deactivateList: false
      });

    this.viewColFacade.onNextAndReset(this.index, 1);
  }

  /**
   * This function is called when AG grid is first initialised and initially
   * set the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    // update grid every time there is an update
    this.subSink.sink = this.store.select(RolePermSelectors.selectUpdateRoleListing)
      .subscribe(a => {
        if (a) {
          if (this.gridApi.getDisplayedRowCount() > 0) {
            this.gridApi.refreshServerSideStore();
          } else {
            this.gridApi.refreshServerSideStore(
              {purge: true}
            )
          }
          // set back to false
          this.store.dispatch(RolePermActions.setUpdateRoleListing({update: false}));
        }
      })
  }

  /**
   * Returns the datasource for the AG grid.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns the datasource
   */
  getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    if (!conditionalCriteria) {
      conditionalCriteria = [
        {columnName: 'calcTotalRecords', operator: '=', value: 'true'}
      ]
    }

    return {
      getRows: grid => {

        // dispatch action to load roles
        this.store.dispatch(RolePermActions.loadRolesInit({request: grid.request}));

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, conditionalCriteria);

        this.subSink.sink = this.tenantRoleService.getByGuidCriteria(pagination, this.apiVisa, this.appletGuid.toString())
          .pipe(
            mergeMap(a => {
              const source: Observable<RoleModel>[] = [];

              a.data.forEach((b) => {

                const newModel: RoleModel = new RoleModel();
                newModel.app_mst_role = b.app_mst_role;
                const users$ = this.getUsersInRole(b.app_mst_role.guid.toString());
                const teams$ = this.getTeamsInRole(b.app_mst_role.guid.toString());
                const createdBy$ = b.app_mst_role.created_by_subject_guid ? this.getUser(b.app_mst_role.created_by_subject_guid.toString()) : of(null);
                const updatedBy$ = b.app_mst_role.updated_by_subject_guid ? this.getUser(b.app_mst_role.updated_by_subject_guid.toString()) : of(null);

                const result$ = zip(users$, teams$, createdBy$, updatedBy$)
                  .pipe(
                    map(([users, teams, createdBy, updatedBy]) => {

                      newModel.num_users = users ? users.totalRecords: 0;
                      newModel.num_teams = teams ? teams.totalRecords: 0;
                      newModel.created_by_name = createdBy ? createdBy.app_login_principal.principal_id.toString() : null;
                      newModel.updated_by_name = updatedBy ? updatedBy.app_login_principal.principal_id.toString() : null;

                      return newModel;
                    })
                  )

                source.push(result$);
              });

              return iif(() => source.length > 0,
              forkJoin(source).pipe(map(x => {
                return {...a,
                data: x}
              })),
              of({...a, data: [], totalRecords: 0}))
            })
          ).subscribe(a => {
            // put data into the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
            // force pagination to go to first page if filter is applied
            if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }

            grid.successCallback(data, totalRecords);
            this.store.dispatch(RolePermActions.loadRolesSuccess({totalRecords: a.totalRecords}));
          }, err => {
            grid.failCallback();
            this.store.dispatch(RolePermActions.loadRolesFailure({error: err.message}));
          })

      }
    }

  }

  /**
   * Get the users that are in this role, specified by roleGuid.
   *
   * @param roleGuid : the role guid to search for
   */
  getUsersInRole(roleGuid: string) {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'app_mst_role_guid', operator: '=', value: roleGuid}
    ]
    const pagination = new Pagination(null, null, criteria);
    return this.tenantUserRoleService.getByCriteria(pagination, this.apiVisa);
  }

  getTeamsInRole(roleGuid: string) {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'mst_role_guid', operator: '=', value: roleGuid}
    ]
    const pagination = new Pagination(null, null, criteria);
    return this.tenantTeamRoleService.getByCriteria(pagination, this.apiVisa);
  }

  /**
   * Return the AppLoginPrincipalContainerModel of the subject with subjectGuid
   * as sepcified in the parameter.
   *
   * @param subjectGuid : the subject guid to the name of
   * @returns an observable of type AppLoginPrincipalContainerModel which
   * contains information of the subject
   */
   getUser(subjectGuid: string) {

    // initialise new pagination
    const pagination = new Pagination();
    // add necessary criteria
    pagination.conditionalCriteria.push({
      columnName: 'guids',
      operator: '=',
      value: subjectGuid
    });

    return this.appLoginSubjectService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map((subject) => {
          subject.data[0]
        })
      )

  }

  /**
   * This function is called every time the user clicks on the toggle column
   * button. It wil trigger the screen to show either one or two columns at a
   * time.
   *
   * @param e : event
   */
   onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  /**
   * This function is called every time the user clicks on a row in the
   * AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
   onRowClicked(entity) {
    // dispatch ngrx actions
    this.store.dispatch(RolePermActions.selectEntity({entity}));
    this.store.dispatch(RolePermActions.selectGuid({guid: entity.app_mst_role.guid.toString()}))
    // deactivate add and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateAdd: false,
          deactivateList: true
        });
      // trigger the next view column
      this.viewColFacade.onNextAndReset(this.index, 2);
    }
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_mst_role hdr
   *   WHERE (hdr.name ILIKE '%x%' OR hdr.code ILIKE '%x%')
   *     AND hdr.status != 'DELETED'
   *
   * GET iam/roles/applet-login-ep/{applet}/query -> RoleUow ->
   * generateWhereClauseByCriteria applies `search_word` as a bound ILIKE over
   * namespace, name, role_type, description and code, and defaults to
   * `status != 'DELETED'`. The listing pages server-side on its own criteria now
   * instead of being handed a fixed guid list.
   *
   * Behaviour note: the keyword also matches namespace, role_type and
   * description, so the search is broader than the old name/code pair.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'calcTotalRecords', operator: '=', value: 'true' },
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * Reset the search bar.
   */
  reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }



}
